export const createDefaultGeneratorConfig = ({
  generatorId,
  controlId,
  profileId,
  outputId,
}) => {
  const safeGeneratorId = generatorId || null;
  const safeControlId = controlId || 'control-default';
  const safeProfileId = profileId || 'profile-default';
  const safeOutputId = outputId || 'out-label';

  return {
    version: 1,
    label: 'Générateur',
    controls: [
      {
        id: safeControlId,
        type: 'check',
        label: 'Option',
        default: true,
        linkedItemId: null,
      },
    ],
    groups: [],
    profiles: [
      {
        id: safeProfileId,
        label: "Combinaison par défaut",
        controlIds: [safeControlId],
        conditionGroups: [
          {
            id: 'group-default',
            mode: 'all',
            controlIds: [safeControlId],
          },
        ],
      },
    ],
    libraries: [],
    outputs: [
      {
        id: safeOutputId,
        label: 'Nom',
        target: { kind: 'item_label' },
        pipeline: [],
        builder: {
          libraryIds: [],
          separator: '',
          capitalize: true,
        },
      },
    ],
    runtime: {
      controlsState: {
        [safeControlId]: true,
      },
    },
    itemCreation: {
      enabled: true,
      openInSidebar: true,
      templateItemId: null,
      outputMapping: {},
    },
    lastResult: null,
    i: safeGeneratorId,
  };
};

export const sanitizeGeneratorConfigForStorage = config => {
  if (!config || typeof config !== 'object') {
    return {};
  }
  return {
    ...config,
    // Generation preview is runtime-only and can explode payload size.
    lastResult: null,
  };
};

export const sanitizeGeneratorEntriesForStorage = entries =>
  (Array.isArray(entries) ? entries : []).map(entry => {
    const key = entry && typeof entry === 'object' ? Object.keys(entry)[0] : null;
    if (!key) {
      return entry;
    }
    const value = entry?.[key];
    if (!value || typeof value !== 'object') {
      return entry;
    }
    return {
      [key]: sanitizeGeneratorConfigForStorage(value),
    };
  });


const normalizeGroupDisplayMode = value => {
  if (value === 'checkbox') {
    return 'checkbox';
  }
  if (value === 'dropdown') {
    return 'dropdown';
  }
  return 'buttons';
};

export const applyGeneratorGroupRules = (controlsStateRaw, groups) => {
  const nextState =
    controlsStateRaw && typeof controlsStateRaw === 'object'
      ? { ...controlsStateRaw }
      : {};

  (Array.isArray(groups) ? groups : []).forEach(group => {
    const groupControlIds = Array.isArray(group?.controlIds)
      ? group.controlIds.filter(Boolean)
      : [];
    if (groupControlIds.length === 0) {
      return;
    }
    const activeIds = groupControlIds.filter(controlId => nextState[controlId] === true);
    if (activeIds.length > 1) {
      const keepId =
        group?.fallbackControlId && activeIds.includes(group.fallbackControlId)
          ? group.fallbackControlId
          : activeIds[0];
      groupControlIds.forEach(controlId => {
        nextState[controlId] = controlId === keepId;
      });
      return;
    }

    if (
      activeIds.length === 0 &&
      group?.fallbackControlId &&
      groupControlIds.includes(group.fallbackControlId)
    ) {
      nextState[group.fallbackControlId] = true;
    }
  });

  return nextState;
};

export const normalizeGeneratorConfig = (rawGenerator, generatorId) => {
  const base =
    rawGenerator && typeof rawGenerator === 'object' ? rawGenerator : {};

  const controls = Array.isArray(base.controls)
    ? base.controls
        .filter(entry => entry && typeof entry === 'object')
        .map(entry => ({
          id: entry.id || null,
          type: entry.type === 'check' ? 'check' : 'check',
          label: entry.label || 'Option',
          default: entry.default !== false,
          linkedItemId:
            typeof entry.linkedItemId === 'string' &&
            entry.linkedItemId.trim().length > 0
              ? entry.linkedItemId
              : null,
        }))
        .filter(entry => entry.id)
    : [];
  const controlIdSet = new Set(controls.map(entry => entry.id));

  const groupsRaw = Array.isArray(base.groups)
    ? base.groups
        .filter(entry => entry && typeof entry === 'object')
        .map(entry => ({
          id: entry.id || null,
          label: entry.label || 'Groupe',
          displayMode: normalizeGroupDisplayMode(entry.displayMode),
          controlIds: Array.isArray(entry.controlIds)
            ? entry.controlIds.filter(controlId => controlIdSet.has(controlId))
            : [],
          fallbackControlId:
            typeof entry.fallbackControlId === 'string'
              ? entry.fallbackControlId
              : null,
        }))
        .filter(entry => entry.id)
    : [];

  const assignedControls = new Set();
  const groups = groupsRaw.map(group => {
    const controlIds = [];
    (group.controlIds || []).forEach(controlId => {
      if (assignedControls.has(controlId)) {
        return;
      }
      assignedControls.add(controlId);
      controlIds.push(controlId);
    });
    return {
      ...group,
      controlIds,
      displayMode: normalizeGroupDisplayMode(group.displayMode),
      fallbackControlId: controlIds.includes(group.fallbackControlId)
        ? group.fallbackControlId
        : null,
    };
  });

  const profiles = Array.isArray(base.profiles)
    ? base.profiles
        .filter(entry => entry && typeof entry === 'object')
        .map(entry => {
          const legacyControlIds = Array.isArray(entry.controlIds)
            ? entry.controlIds.filter(Boolean)
            : [];
          const conditionGroupsRaw = Array.isArray(entry.conditionGroups)
            ? entry.conditionGroups
            : [];
          const conditionGroups =
            conditionGroupsRaw.length > 0
              ? conditionGroupsRaw
                  .filter(group => group && typeof group === 'object')
                  .map(group => ({
                    id: group.id || null,
                    mode: group.mode === 'any' ? 'any' : 'all',
                    controlIds: Array.isArray(group.controlIds)
                      ? group.controlIds.filter(Boolean)
                      : [],
                  }))
                  .filter(group => group.id)
              : [
                  {
                    id: 'group-legacy',
                    mode: entry.matchMode === 'any' ? 'any' : 'all',
                    controlIds: legacyControlIds,
                  },
                ];
          return {
            id: entry.id || null,
            label: entry.label || 'Combinaison',
            matchMode: entry.matchMode === 'any' ? 'any' : 'all',
            controlIds: legacyControlIds,
            conditionGroups,
          };
        })
        .filter(entry => entry.id)
    : [];

  const controlsStateBase =
    base.runtime && typeof base.runtime.controlsState === 'object'
      ? base.runtime.controlsState
      : {};
  const controlsState = {};
  controls.forEach(control => {
    if (typeof controlsStateBase[control.id] === 'boolean') {
      controlsState[control.id] = controlsStateBase[control.id];
      return;
    }
    controlsState[control.id] = control.default !== false;
  });
  const controlsStateWithGroups = applyGeneratorGroupRules(controlsState, groups);

  const outputs = Array.isArray(base.outputs)
    ? base.outputs
        .filter(entry => entry && typeof entry === 'object')
        .map(entry => {
          const builderRaw =
            entry.builder && typeof entry.builder === 'object'
              ? entry.builder
              : {};
          const numberRaw =
            entry.number && typeof entry.number === 'object'
              ? entry.number
              : {};
          return {
            ...entry,
            id: entry.id || null,
            label: entry.label || 'Sortie',
            target:
              entry.target && typeof entry.target === 'object'
                ? entry.target
                : { kind: 'text' },
            profileIds: Array.isArray(entry.profileIds)
              ? entry.profileIds.filter(Boolean)
              : [],
            markdownTemplate:
              typeof entry.markdownTemplate === 'string'
                ? entry.markdownTemplate
                : '',
            pipeline: Array.isArray(entry.pipeline) ? entry.pipeline : [],
            builder: {
              libraryIds: Array.isArray(builderRaw.libraryIds)
                ? builderRaw.libraryIds.filter(Boolean)
                : [],
              separator:
                typeof builderRaw.separator === 'string'
                  ? builderRaw.separator
                  : '',
              capitalize: builderRaw.capitalize !== false,
            },
            number: {
              min:
                Number.isFinite(Number(numberRaw.min))
                  ? Number(numberRaw.min)
                  : 0,
              max:
                Number.isFinite(Number(numberRaw.max))
                  ? Number(numberRaw.max)
                  : 10,
              step:
                Number.isFinite(Number(numberRaw.step)) &&
                Number(numberRaw.step) > 0
                  ? Number(numberRaw.step)
                  : 1,
            },
            sourceGeneratorId:
              typeof entry.sourceGeneratorId === 'string' &&
              entry.sourceGeneratorId.trim().length > 0
                ? entry.sourceGeneratorId
                : null,
            sourceControlIds: Array.isArray(entry.sourceControlIds)
              ? entry.sourceControlIds.filter(Boolean)
              : [],
          };
        })
        .filter(entry => entry.id)
    : [];

  const libraries = Array.isArray(base.libraries)
    ? base.libraries
        .filter(entry => entry && typeof entry === 'object')
        .map(entry => ({
          id: entry.id || null,
          label: entry.label || 'Table',
          code:
            typeof entry.code === 'string' && entry.code.trim().length > 0
              ? entry.code.trim()
              : '',
          entries: Array.isArray(entry.entries)
            ? entry.entries
            : typeof entry.entries === 'string'
              ? entry.entries
                  .split('\n')
                  .map(v => v.trim())
                  .filter(Boolean)
              : [],
          entrySeparator:
            entry.entrySeparator === 'newline' ? 'newline' : 'comma',
          mergeMode: entry.mergeMode === 'remove' ? 'remove' : 'add',
          profileIds: Array.isArray(entry.profileIds)
            ? entry.profileIds.filter(Boolean)
            : [],
        }))
        .filter(entry => entry.id)
    : [];

  return {
    version: Number.isFinite(Number(base.version)) ? Number(base.version) : 1,
    label: base.label || 'Générateur',
    controls,
    groups,
    profiles,
    libraries,
    outputs,
    runtime: {
      controlsState: controlsStateWithGroups,
    },
    itemCreation: {
      enabled: base?.itemCreation?.enabled !== false,
      openInSidebar: base?.itemCreation?.openInSidebar !== false,
      templateItemId:
        typeof base?.itemCreation?.templateItemId === 'string' &&
        base.itemCreation.templateItemId.trim().length > 0
          ? base.itemCreation.templateItemId
          : null,
      outputMapping:
        base?.itemCreation?.outputMapping &&
        typeof base.itemCreation.outputMapping === 'object'
          ? { ...base.itemCreation.outputMapping }
          : {},
    },
    lastResult:
      base.lastResult && typeof base.lastResult === 'object'
        ? base.lastResult
        : null,
    i: base.i || generatorId || null,
  };
};

const sortIds = ids => [...(Array.isArray(ids) ? ids : [])].sort();

const getSelectedControlIds = generatorConfig => {
  const controlsState =
    generatorConfig?.runtime &&
    typeof generatorConfig.runtime.controlsState === 'object'
      ? generatorConfig.runtime.controlsState
      : {};
  return sortIds(
    Object.keys(controlsState).filter(controlId => controlsState[controlId] === true),
  );
};

export const resolveActiveProfileFromControls = generatorConfig => {
  const selectedControlIds = getSelectedControlIds(generatorConfig);

  const profiles = Array.isArray(generatorConfig?.profiles)
    ? generatorConfig.profiles
    : [];
  for (const profile of profiles) {
    const profileIds = sortIds(profile?.controlIds?.filter(Boolean) || []);
    if (profileIds.length !== selectedControlIds.length) {
      continue;
    }
    let ok = true;
    for (let i = 0; i < profileIds.length; i++) {
      if (profileIds[i] !== selectedControlIds[i]) {
        ok = false;
        break;
      }
    }
    if (ok) {
      return profile || null;
    }
  }
  return null;
};

export const resolveMatchingProfilesFromControls = generatorConfig => {
  const selectedControlIds = getSelectedControlIds(generatorConfig);
  const selectedSet = new Set(selectedControlIds);
  const profiles = Array.isArray(generatorConfig?.profiles)
    ? generatorConfig.profiles
    : [];

  return profiles
    .map((profile, index) => {
      const normalizedGroups = Array.isArray(profile?.conditionGroups)
        ? profile.conditionGroups
            .filter(group => group && typeof group === 'object')
            .map(group => ({
              mode: group.mode === 'any' ? 'any' : 'all',
              controlIds: sortIds(group?.controlIds?.filter(Boolean) || []),
            }))
            .filter(group => group.controlIds.length > 0)
        : [];
      const fallbackControlIds = sortIds(profile?.controlIds?.filter(Boolean) || []);
      const groups =
        normalizedGroups.length > 0
          ? normalizedGroups
          : fallbackControlIds.length > 0
            ? [
                {
                  mode: profile?.matchMode === 'any' ? 'any' : 'all',
                  controlIds: fallbackControlIds,
                },
              ]
            : [];
      const specificityScore = groups.reduce((acc, group) => {
        if (group.mode === 'any') {
          return acc + 1;
        }
        return acc + group.controlIds.length;
      }, 0);
      return {
        profile,
        index,
        groups,
        specificityScore,
      };
    })
    .filter(entry => {
      if (!entry.profile || !entry.profile.id) {
        return false;
      }
      // "Simple mode": a profile without any condition is always eligible.
      if (entry.groups.length === 0) {
        return true;
      }
      return entry.groups.every(group => {
        if (group.mode === 'any') {
          return group.controlIds.some(controlId => selectedSet.has(controlId));
        }
        return group.controlIds.every(controlId => selectedSet.has(controlId));
      });
    })
    .sort((a, b) => {
      if (b.specificityScore !== a.specificityScore) {
        return b.specificityScore - a.specificityScore;
      }
      return a.index - b.index;
    })
    .map(entry => entry.profile);
};

const pickRandom = values => {
  if (!Array.isArray(values) || values.length === 0) {
    return '';
  }
  return values[Math.floor(Math.random() * values.length)] || '';
};

const capitalizeFirst = str => {
  if (typeof str !== 'string' || str.length === 0) {
    return '';
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const countDecimals = value => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const str = String(value);
  if (!str.includes('.')) {
    return 0;
  }
  return str.split('.')[1]?.length || 0;
};

const generateSteppedNumber = numberConfig => {
  const minRaw = Number(numberConfig?.min);
  const maxRaw = Number(numberConfig?.max);
  const stepRaw = Number(numberConfig?.step);

  const safeMin = Number.isFinite(minRaw) ? minRaw : 0;
  const safeMax = Number.isFinite(maxRaw) ? maxRaw : 10;
  const safeStep =
    Number.isFinite(stepRaw) && stepRaw > 0 ? stepRaw : 1;

  const low = Math.min(safeMin, safeMax);
  const high = Math.max(safeMin, safeMax);
  const stepsCount = Math.max(0, Math.floor((high - low) / safeStep));
  const randomStep =
    stepsCount > 0 ? Math.floor(Math.random() * (stepsCount + 1)) : 0;
  const raw = low + randomStep * safeStep;

  const precision = Math.min(
    6,
    Math.max(countDecimals(low), countDecimals(high), countDecimals(safeStep)),
  );

  return Number(raw.toFixed(precision));
};

const resolveLibraryFromToken = ({
  token,
  librariesByLabel,
  librariesByCode,
  librariesById,
}) => {
  const key = String(token || '').trim();
  if (!key) {
    return null;
  }
  return (
    librariesByLabel[key] ||
    librariesByLabel[key.toLowerCase()] ||
    librariesByCode[key] ||
    librariesByCode[key.toLowerCase()] ||
    librariesById[key] ||
    librariesById[key.toLowerCase()] ||
    null
  );
};

const resolveTemplateValue = ({
  rawValue,
  librariesByLabel,
  librariesByCode,
  librariesById,
  depth = 0,
  stack = [],
}) => {
  const source = typeof rawValue === 'string' ? rawValue : '';
  if (!source) {
    return '';
  }
  if (depth > 6) {
    return source;
  }

  return source.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, token) => {
    const lib = resolveLibraryFromToken({
      token,
      librariesByLabel,
      librariesByCode,
      librariesById,
    });
    if (!lib) {
      return '';
    }
    const libId = lib?.id || null;
    if (libId && stack.includes(libId)) {
      return '';
    }
    const nextStack = libId ? [...stack, libId] : stack;
    const picked = pickRandomLibraryEntry({
      lib,
      librariesByLabel,
      librariesByCode,
      librariesById,
      stack: nextStack,
    });
    if (!picked) {
      return '';
    }
    return resolveTemplateValue({
      rawValue: picked,
      librariesByLabel,
      librariesByCode,
      librariesById,
      depth: depth + 1,
      stack: nextStack,
    });
  });
};

const pickRandomLibraryEntry = ({
  lib,
  librariesByLabel,
  librariesByCode,
  librariesById,
  stack = [],
}) => {
  const entries = Array.isArray(lib?.entries) ? lib.entries : [];
  if (entries.length === 0) {
    return '';
  }
  const removeRegexList = Array.isArray(lib?.removeRegexList)
    ? lib.removeRegexList
    : [];
  if (removeRegexList.length === 0) {
    return pickRandom(entries);
  }
  const candidates = entries.filter(rawEntry => {
    const rawValue = String(rawEntry || '').trim();
    if (!rawValue) {
      return false;
    }
    if (removeRegexList.some(regex => regex.test(rawValue))) {
      return false;
    }
    const resolvedValue = resolveTemplateValue({
      rawValue,
      librariesByLabel,
      librariesByCode,
      librariesById,
      stack,
    });
    if (!resolvedValue) {
      return false;
    }
    return !removeRegexList.some(regex => regex.test(String(resolvedValue).trim()));
  });
  if (candidates.length === 0) {
    return '';
  }
  return pickRandom(candidates);
};

const executeOutputPipeline = ({
  output,
  librariesById,
  librariesByLabel,
  librariesByCode,
  variables,
}) => {
  const pipeline = Array.isArray(output?.pipeline) ? output.pipeline : [];
  let parts = [];

  for (const step of pipeline) {
    if (!step || typeof step !== 'object') {
      continue;
    }
    const op = step.op || '';
    if (op === 'pick') {
      const lib = step.libraryId ? librariesById[step.libraryId] : null;
      const rawPicked = pickRandomLibraryEntry({
        lib,
        librariesByLabel,
        librariesByCode,
        librariesById,
        stack: lib?.id ? [lib.id] : [],
      });
      const picked = resolveTemplateValue({
        rawValue: rawPicked,
        librariesByLabel,
        librariesByCode,
        librariesById,
        stack: lib?.id ? [lib.id] : [],
      });
      if (picked) {
        parts.push(String(picked));
      }
      continue;
    }
    if (op === 'literal') {
      if (typeof step.value === 'string') {
        parts.push(step.value);
      }
      continue;
    }
    if (op === 'template') {
      const rawTemplate =
        typeof step.value === 'string' ? step.value : '';
      const rendered = rawTemplate.replace(/\{([^}]+)\}/g, (_, token) => {
        const key = String(token || '').trim();
        return Object.prototype.hasOwnProperty.call(variables, key)
          ? String(variables[key] ?? '')
          : '';
      });
      if (rendered) {
        parts.push(rendered);
      }
      continue;
    }
    if (op === 'join') {
      const sep = typeof step.sep === 'string' ? step.sep : '';
      parts = [parts.join(sep)];
      continue;
    }
    if (op === 'capitalize') {
      const current = parts.length > 0 ? parts.join('') : '';
      parts = [capitalizeFirst(current)];
    }
  }

  return parts.join('').trim();
};

const executeOutputBuilder = ({
  output,
  librariesById,
  librariesByLabel,
  librariesByCode,
}) => {
  const builder =
    output?.builder && typeof output.builder === 'object' ? output.builder : {};
  const libraryIds = Array.isArray(builder.libraryIds)
    ? builder.libraryIds.filter(Boolean)
    : [];
  const parts = libraryIds.map(libraryId => {
    const lib = librariesById[libraryId];
    const stack = lib?.id ? [lib.id] : [];
    return pickRandomLibraryEntry({
      lib,
      librariesByLabel,
      librariesByCode,
      librariesById,
      stack,
    });
  });
  const sep = typeof builder.separator === 'string' ? builder.separator : '';
  const joined = parts.filter(Boolean).join(sep);
  return builder.capitalize === false ? joined : capitalizeFirst(joined);
};

const executeOutputMarkdownTemplate = ({
  output,
  librariesById,
  librariesByLabel,
  librariesByCode,
}) => {
  const template =
    typeof output?.markdownTemplate === 'string' ? output.markdownTemplate : '';
  if (!template.trim()) {
    return '';
  }

  return resolveTemplateValue({
    rawValue: template,
    librariesByLabel,
    librariesByCode,
    librariesById,
  });
};

const executeOutputBuilderResolved = ({
  output,
  librariesById,
  librariesByLabel,
  librariesByCode,
}) => {
  const raw = executeOutputBuilder({
    output,
    librariesById,
    librariesByLabel,
    librariesByCode,
  });
  return resolveTemplateValue({
    rawValue: raw,
    librariesByLabel,
    librariesByCode,
    librariesById,
  });
};

const buildMatchedProfileContext = generator => {
  const matchingProfiles = resolveMatchingProfilesFromControls(generator);
  const activeProfile =
    matchingProfiles[0] || resolveActiveProfileFromControls(generator);
  const activeProfileId = activeProfile?.id || null;
  const matchedProfileIdSet = new Set(
    matchingProfiles.map(profile => profile?.id).filter(Boolean),
  );
  const profileRank = {};
  matchingProfiles.forEach((profile, index) => {
    if (profile?.id) {
      profileRank[profile.id] = index;
    }
  });
  return {
    matchingProfiles,
    activeProfileId,
    matchedProfileIdSet,
    profileRank,
  };
};

const buildScopedLibraries = (libraries, matchedProfileIdSet) =>
  (Array.isArray(libraries) ? libraries : []).filter(lib => {
    const profileIds = Array.isArray(lib?.profileIds)
      ? lib.profileIds.filter(Boolean)
      : [];
    if (profileIds.length === 0) {
      return true;
    }
    return profileIds.some(profileId => matchedProfileIdSet.has(profileId));
  });

const escapeRegExp = value => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildTemplateAwareRemovalRegex = rawValue => {
  const value = String(rawValue || '').trim();
  if (!value) {
    return null;
  }
  // `{{token}}` behaves as a wildcard segment in removal rules.
  const source = escapeRegExp(value).replace(/\\\{\\\{.*?\\\}\\\}/g, '.+?');
  try {
    return new RegExp(`^${source}$`, 'i');
  } catch (error) {
    return null;
  }
};

const buildLibrariesIndex = scopedLibraries => {
  const mergedLibrariesByLabelKey = {};
  const libraryLabelKeyById = {};
  scopedLibraries.forEach(lib => {
    const rawLabel = typeof lib?.label === 'string' ? lib.label.trim() : '';
    if (!rawLabel) {
      return;
    }
    const labelKey = rawLabel.toLowerCase();
    libraryLabelKeyById[lib.id] = labelKey;
    if (!mergedLibrariesByLabelKey[labelKey]) {
      mergedLibrariesByLabelKey[labelKey] = {
        id: lib.id,
        label: rawLabel,
        addEntries: [],
        removeEntries: new Set(),
      };
    }
    const sourceEntries = Array.isArray(lib?.entries) ? lib.entries : [];
    const legacyRemoveMode = lib?.mergeMode === 'remove';
    sourceEntries.forEach(rawValue => {
      const value = typeof rawValue === 'string' ? rawValue.trim() : '';
      if (!value) {
        return;
      }
      if (legacyRemoveMode) {
        mergedLibrariesByLabelKey[labelKey].removeEntries.add(value);
        return;
      }
      if (value.startsWith('-')) {
        const toRemove = value.slice(1).trim();
        if (toRemove) {
          mergedLibrariesByLabelKey[labelKey].removeEntries.add(toRemove);
        }
        return;
      }
      mergedLibrariesByLabelKey[labelKey].addEntries.push(value);
    });
  });
  const mergedLibrariesFinalByLabelKey = {};
  Object.keys(mergedLibrariesByLabelKey).forEach(labelKey => {
    const entry = mergedLibrariesByLabelKey[labelKey];
    const removed = entry?.removeEntries || new Set();
    const removedNormalized = new Set(
      Array.from(removed).map(value => String(value || '').trim().toLowerCase()),
    );
    const removedRegexList = Array.from(removed)
      .map(buildTemplateAwareRemovalRegex)
      .filter(Boolean);
    mergedLibrariesFinalByLabelKey[labelKey] = {
      id: entry?.id || null,
      label: entry?.label || '',
      removeRegexList: removedRegexList,
      entries: (entry?.addEntries || []).filter(value => {
        if (removed.has(value)) {
          return false;
        }
        const normalized = String(value || '').trim().toLowerCase();
        if (removedNormalized.has(normalized)) {
          return false;
        }
        return !removedRegexList.some(regex => regex.test(String(value || '').trim()));
      }),
    };
  });

  const librariesById = {};
  const librariesByLabel = {};
  const librariesByCode = {};
  scopedLibraries.forEach(lib => {
    if (lib?.id) {
      const labelKey = libraryLabelKeyById[lib.id];
      const mergedLib = labelKey ? mergedLibrariesFinalByLabelKey[labelKey] : null;
      const effectiveLib = mergedLib || lib;
      librariesById[lib.id] = effectiveLib;
      librariesById[String(lib.id).toLowerCase()] = effectiveLib;
    }
    if (typeof lib?.label === 'string' && lib.label.trim()) {
      const label = lib.label.trim();
      const labelKey = label.toLowerCase();
      const mergedLib = mergedLibrariesFinalByLabelKey[labelKey];
      const effectiveLib = mergedLib || lib;
      librariesByLabel[label] = effectiveLib;
      librariesByLabel[labelKey] = effectiveLib;
    }
    if (typeof lib?.code === 'string' && lib.code.trim()) {
      const code = lib.code.trim();
      const labelKey = libraryLabelKeyById[lib.id];
      const mergedLib = labelKey ? mergedLibrariesFinalByLabelKey[labelKey] : null;
      const effectiveLib = mergedLib || lib;
      librariesByCode[code] = effectiveLib;
      librariesByCode[code.toLowerCase()] = effectiveLib;
    }
  });

  return {
    librariesById,
    librariesByLabel,
    librariesByCode,
  };
};

const buildScopedOutputs = (outputs, matchedProfileIdSet, profileRank) => {
  const outputList = Array.isArray(outputs) ? outputs : [];
  const scopedOutputs = outputList
    .filter(output => {
      const profileIds = Array.isArray(output?.profileIds)
        ? output.profileIds.filter(Boolean)
        : [];
      return profileIds.some(profileId => matchedProfileIdSet.has(profileId));
    })
    .sort((a, b) => {
      const profileIdsA = Array.isArray(a?.profileIds) ? a.profileIds : [];
      const profileIdsB = Array.isArray(b?.profileIds) ? b.profileIds : [];
      const rankA = profileIdsA.reduce((acc, profileId) => {
        const next = profileRank[profileId];
        if (next === undefined) {
          return acc;
        }
        return Math.min(acc, next);
      }, Number.POSITIVE_INFINITY);
      const rankB = profileIdsB.reduce((acc, profileId) => {
        const next = profileRank[profileId];
        if (next === undefined) {
          return acc;
        }
        return Math.min(acc, next);
      }, Number.POSITIVE_INFINITY);
      if (rankA !== rankB) {
        return rankA - rankB;
      }
      return 0;
    });
  const genericOutputs = outputList.filter(output => {
    const profileIds = Array.isArray(output?.profileIds)
      ? output.profileIds.filter(Boolean)
      : [];
    return profileIds.length === 0;
  });
  const hasScopedItemLabel = scopedOutputs.some(
    output => output?.target?.kind === 'item_label',
  );
  return [
    ...scopedOutputs,
    ...genericOutputs.filter(output =>
      hasScopedItemLabel ? output?.target?.kind !== 'item_label' : true,
    ),
  ];
};

const buildNestedGeneratorValue = (nestedResult, targetKind) => {
  const nestedValues = Array.isArray(nestedResult?.values)
    ? nestedResult.values
    : [];
  if (targetKind === 'item_label') {
    const labels = nestedValues
      .filter(entry => entry?.target?.kind === 'item_label')
      .map(entry => (typeof entry?.value === 'string' ? entry.value.trim() : ''))
      .filter(Boolean);
    if (labels.length > 0) {
      return labels.join(' ');
    }
    return typeof nestedResult?.summaryLabel === 'string'
      ? nestedResult.summaryLabel
      : '';
  }
  const texts = nestedValues
    .filter(entry => entry?.target?.kind !== 'num')
    .map(entry => (typeof entry?.value === 'string' ? entry.value : ''))
    .filter(value => value.trim().length > 0);
  return texts.join('\n').trim();
};

export const runGenerator = (rawGenerator, options = {}) => {
  const generatorsById =
    options && typeof options.generatorsById === 'object'
      ? options.generatorsById
      : {};
  const visitedGeneratorIds = Array.isArray(options?.visitedGeneratorIds)
    ? options.visitedGeneratorIds.filter(Boolean)
    : [];
  const generator = normalizeGeneratorConfig(rawGenerator);
  const forcedControlIds = Array.isArray(options?.forcedControlIds)
    ? options.forcedControlIds.filter(Boolean)
    : [];
  if (forcedControlIds.length > 0) {
    const allowedControlIds = new Set((generator.controls || []).map(control => control.id));
    const forcedSet = new Set(
      forcedControlIds.filter(controlId => allowedControlIds.has(controlId)),
    );
    const nextControlsState = {};
    (generator.controls || []).forEach(control => {
      nextControlsState[control.id] = forcedSet.has(control.id);
    });
    generator.runtime = {
      ...(generator.runtime || {}),
      controlsState: applyGeneratorGroupRules(nextControlsState, generator.groups || []),
    };
  }
  const currentGeneratorId = generator?.i || null;
  const nextVisitedGeneratorIds = currentGeneratorId
    ? Array.from(new Set([...visitedGeneratorIds, currentGeneratorId]))
    : visitedGeneratorIds;
  const controlsState =
    generator.runtime && typeof generator.runtime.controlsState === 'object'
      ? generator.runtime.controlsState
      : {};
  const { activeProfileId, matchedProfileIdSet, profileRank } =
    buildMatchedProfileContext(generator);
  // Keep libraries constrained to matching profiles so controls stay authoritative.
  // Inside this scope, tables are still merged by label.
  const scopedLibraries = buildScopedLibraries(
    generator.libraries,
    matchedProfileIdSet,
  );
  const { librariesById, librariesByLabel, librariesByCode } =
    buildLibrariesIndex(scopedLibraries);

  const defaultLabel = '';

  const mergedOutputs = buildScopedOutputs(
    generator.outputs,
    matchedProfileIdSet,
    profileRank,
  );

  const effectiveOutputs =
    mergedOutputs.length > 0
      ? mergedOutputs
      : [
          {
            id: 'out_label',
            label: 'Nom',
            target: { kind: 'item_label' },
            pipeline: [],
            builder: {
              libraryIds: [],
              separator: '',
              capitalize: true,
            },
          },
        ];

  const variables = {};
  const values = effectiveOutputs.map(output => {
    const targetKind = output?.target?.kind || 'text';
    let value = '';
    if (targetKind === 'num') {
      value = generateSteppedNumber(output?.number || {});
    } else {
      const nestedGeneratorId =
        typeof output?.sourceGeneratorId === 'string' &&
        output.sourceGeneratorId.trim().length > 0
          ? output.sourceGeneratorId.trim()
          : null;
      if (
        nestedGeneratorId &&
        !nextVisitedGeneratorIds.includes(nestedGeneratorId) &&
        generatorsById?.[nestedGeneratorId]
      ) {
        const nestedForcedControlIds = Array.isArray(output?.sourceControlIds)
          ? output.sourceControlIds.filter(Boolean)
          : [];
        const nestedResult = runGenerator(generatorsById[nestedGeneratorId], {
          generatorsById,
          visitedGeneratorIds: nextVisitedGeneratorIds,
          forcedControlIds: nestedForcedControlIds,
        });
        value = buildNestedGeneratorValue(nestedResult, targetKind);
      }
      if (targetKind !== 'item_label') {
        if (!value) {
          value = executeOutputMarkdownTemplate({
            output,
            librariesById,
            librariesByLabel,
            librariesByCode,
          });
        }
      }
      if (!value) {
        value = executeOutputPipeline({
          output,
          librariesById,
          librariesByLabel,
          librariesByCode,
          variables,
        });
      }
      if (!value) {
        value = executeOutputBuilderResolved({
          output,
          librariesById,
          librariesByLabel,
          librariesByCode,
        });
      }
    }
    if (targetKind !== 'num' && !value) {
      value = targetKind === 'item_label' ? defaultLabel : '';
    }
    if (targetKind === 'item_label') {
      value = value || defaultLabel;
    }
    variables[output.id] =
      targetKind === 'num' ? String(value ?? '') : value;
    return {
      outputId: output.id || null,
      label: output.label || 'Sortie',
      target: output.target || { kind: 'text' },
      value: targetKind === 'num' ? value : value || '',
    };
  });

  const labelValues = values
    .filter(entry => entry?.target?.kind === 'item_label')
    .map(entry => (typeof entry?.value === 'string' ? entry.value.trim() : ''))
    .filter(Boolean);

  return {
    at: Date.now(),
    activeProfileId,
    controlsState: { ...controlsState },
    values,
    summaryLabel: labelValues.length > 0 ? labelValues.join(' ') : defaultLabel,
  };
};
